import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { PlusIcon, PencilSquareIcon, TrashIcon, EyeIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

interface Lesson {
  id: string
  date: string
  subjects: {
    math?: {
      topic: string
      content: string
      homework: string
    }
    thai?: {
      topic: string
      content: string
      homework: string
    }
    english?: {
      topic: string
      content: string
      homework: string
    }
  }
  status: 'draft' | 'published'
  publishedAt?: string
  studentsNotified: number
}

export function AdminLessonsPage() {
  const navigate = useNavigate()
  const [lessons] = useState<Lesson[]>([
    {
      id: '1',
      date: '2024-08-17',
      subjects: {
        math: {
          topic: 'การบวกลบเลข 3 หลัก',
          content: 'ทบทวนการบวกตัวเลข 3 หลัก, การทดในหลักสิบ',
          homework: 'แบบฝึกหัดหน้า 25 ข้อ 1-10'
        },
        thai: {
          topic: 'การผันวรรณยุกต์',
          content: 'วรรณยุกต์ 5 เสียง, ฝึกผันคำ',
          homework: 'อ่านเรื่อง "บ้านของเรา"'
        },
        english: {
          topic: 'Present Simple Tense',
          content: 'I, You, We + V1, He, She, It + Vs',
          homework: 'Workbook page 15'
        }
      },
      status: 'published',
      publishedAt: '2024-08-17T14:30:00',
      studentsNotified: 18
    },
    {
      id: '2',
      date: '2024-08-10',
      subjects: {
        math: {
          topic: 'การคูณ',
          content: 'สูตรคูณแม่ 2-5',
          homework: 'ท่องสูตรคูณแม่ 2-5'
        },
        thai: {
          topic: 'คำควบกล้ำ',
          content: 'คำควบกล้ำแท้และไม่แท้',
          homework: 'แบบฝึกหัดหน้า 20'
        },
        english: {
          topic: 'Colors and Shapes',
          content: 'Basic colors, Common shapes',
          homework: 'Draw and label 5 shapes'
        }
      },
      status: 'published',
      publishedAt: '2024-08-10T15:00:00',
      studentsNotified: 17
    }
  ])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const thaiMonths = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    const thaiYear = date.getFullYear() + 543
    return `${date.getDate()} ${thaiMonths[date.getMonth()]} ${thaiYear}`
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="จัดการบทเรียน"
        showBackButton={true}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Post Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/lessons/post')}
            className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            โพสต์บทเรียนวันนี้
          </button>
        </div>

        {/* Lessons List */}
        <div className="space-y-4">
          {lessons.map((lesson) => (
            <div key={lesson.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      บทเรียนวันที่ {formatDate(lesson.date)}
                    </h3>
                    {lesson.status === 'published' && lesson.publishedAt && (
                      <p className="text-sm text-gray-500 mt-1">
                        โพสต์เมื่อ {formatTime(lesson.publishedAt)} • แจ้งผู้ปกครอง {lesson.studentsNotified} คน
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {lesson.status === 'published' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        เผยแพร่แล้ว
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        แบบร่าง
                      </span>
                    )}
                  </div>
                </div>

                {/* Subject Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {lesson.subjects.math && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <h4 className="font-medium text-blue-900 mb-1">🔢 คณิตศาสตร์</h4>
                      <p className="text-sm text-blue-700">{lesson.subjects.math.topic}</p>
                      {lesson.subjects.math.homework && (
                        <p className="text-xs text-blue-600 mt-1">📝 {lesson.subjects.math.homework}</p>
                      )}
                    </div>
                  )}
                  
                  {lesson.subjects.thai && (
                    <div className="bg-green-50 rounded-lg p-3">
                      <h4 className="font-medium text-green-900 mb-1">🇹🇭 ภาษาไทย</h4>
                      <p className="text-sm text-green-700">{lesson.subjects.thai.topic}</p>
                      {lesson.subjects.thai.homework && (
                        <p className="text-xs text-green-600 mt-1">📝 {lesson.subjects.thai.homework}</p>
                      )}
                    </div>
                  )}
                  
                  {lesson.subjects.english && (
                    <div className="bg-purple-50 rounded-lg p-3">
                      <h4 className="font-medium text-purple-900 mb-1">🔤 ภาษาอังกฤษ</h4>
                      <p className="text-sm text-purple-700">{lesson.subjects.english.topic}</p>
                      {lesson.subjects.english.homework && (
                        <p className="text-xs text-purple-600 mt-1">📝 {lesson.subjects.english.homework}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-200">
                  <button
                    onClick={() => navigate(`/admin/lessons/${lesson.id}`)}
                    className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    title="ดูรายละเอียด"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => navigate(`/admin/lessons/${lesson.id}/edit`)}
                    className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                    title="แก้ไข"
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => navigate('/admin/delete-confirm', {
                      state: {
                        type: 'lesson',
                        item: {
                          id: lesson.id,
                          name: `บทเรียนวันที่ ${formatDate(lesson.date)}`,
                          description: `${Object.keys(lesson.subjects).length} วิชา`,
                          details: Object.entries(lesson.subjects).map(([subject, data]) => 
                            `${subject === 'math' ? 'คณิตศาสตร์' : subject === 'thai' ? 'ภาษาไทย' : 'ภาษาอังกฤษ'}: ${data?.topic || 'ไม่ระบุ'}`
                          )
                        },
                        returnPath: '/admin/lessons'
                      }
                    })}
                    className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                    title="ลบ"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {lessons.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <PencilSquareIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">ยังไม่มีบทเรียน</h3>
            <p className="text-gray-500 mb-4">เริ่มต้นโพสต์บทเรียนแรกของคุณ</p>
            <button
              onClick={() => navigate('/admin/lessons/post')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              โพสต์บทเรียน
            </button>
          </div>
        )}
      </div>

      <TabNavigation activeTab="lessons" />
    </div>
  )
}